import ApplicationController from "./application_controller"
import Logger from '../lib/logger'
import PushSubscriptionManager from "../services/push_subscription_manager"

export default class extends ApplicationController {
  static targets = ["button", "icon", "status", "count", "dropdownCount", "soundStatus", "pushStatus"]
  static values = {
    currentPermission: String,
    enabled: Boolean,
    soundUrl: { type: String, default: '' },
    soundVolume: { type: Number, default: 0.3 },
    vapidPublicKey: String,
    pushEnabled: { type: Boolean, default: false }
  }

  connect() {
    super.connect()
    Logger.debug('Notifications controller connected')
    this.updateButtonState()
    this.subscribeToNotifications()
    this.preloadNotificationSound()
    this.initializePushNotifications()
  }

  disconnect() {
    super.disconnect()
    Logger.debug('Notifications controller disconnected')
    if (this.notificationsSubscription) {
      this.notificationsSubscription.unsubscribe()
    }
  }

  // ========================================
  // PUSH NOTIFICATION SETUP
  // ========================================

  async initializePushNotifications() {
    // Get VAPID key from meta tag or value
    const vapidKey = this.vapidPublicKeyValue ||
                     document.querySelector('meta[name="vapid-public-key"]')?.content
    
    if (!vapidKey) {
      Logger.debug('VAPID key not found, push notifications disabled')
      return
    }

    this.pushManager = new PushSubscriptionManager({
      vapidPublicKey: vapidKey
    })

    // Check if push is available
    if (!this.pushManager.isAvailable()) {
      Logger.debug('Push notifications not available in this browser')
      return
    }

    // Register service worker
    const registered = await this.pushManager.register()
    if (!registered) {
      Logger.debug('Failed to register service worker')
      return
    }

    // Check current subscription status
    const subscription = await this.pushManager.getSubscription()
    this.pushEnabledValue = subscription !== null
    this.updatePushStatus()
    
    Logger.debug('Push notifications initialized, subscribed:', this.pushEnabledValue)
  }

  async togglePushNotifications() {
    if (!this.pushManager) {
      this.showAlert('Push notifications are not available', 'warning')
      return
    }

    if (this.pushEnabledValue) {
      // Unsubscribe
      const success = await this.pushManager.unsubscribe()
      if (success) {
        this.pushEnabledValue = false
        this.updatePushStatus()
        this.showAlert('Push notifications disabled', 'success')
      } else {
        this.showAlert('Failed to disable push notifications', 'danger')
      }
    } else {
      // Subscribe
      const subscription = await this.pushManager.subscribe()
      if (subscription) {
        this.pushEnabledValue = true
        this.updatePushStatus()
        this.showAlert('Push notifications enabled', 'success')
      } else {
        this.showAlert('Failed to enable push notifications. Please grant notification permission.', 'warning')
      }
    }
  }

  updatePushStatus() {
    if (this.hasPushStatusTarget) {
      this.pushStatusTarget.textContent = this.pushEnabledValue ? 'Enabled' : 'Disabled'
    }
  }

  async testPushNotification() {
    if (!this.pushManager || !this.pushEnabledValue) {
      this.showAlert('Please enable push notifications first', 'warning')
      return
    }

    const success = await this.pushManager.showNotification('Test Push Notification', {
      body: 'This is a test push notification from your ERP system.',
      icon: '/assets/logo.png',
      tag: 'test-push-notification',
      requireInteraction: false
    })

    if (success) {
      this.showAlert('Test push notification sent!', 'success')
    } else {
      this.showAlert('Failed to send test push notification', 'danger')
    }
  }

  // Request browser notification permission
  async requestPermission() {
    Logger.debug('Requesting notification permission...')
    
    try {
      // Check if notifications are supported
      if (!('Notification' in window)) {
        this.showAlert('Browser notifications are not supported in this browser.', 'warning')
        return
      }

      // Check current permission status
      if (Notification.permission === 'granted') {
        this.showAlert('Browser notifications are already enabled!', 'info')
        return
      }

      if (Notification.permission === 'denied') {
        this.showAlert('Browser notifications are blocked. Please enable them in your browser settings.', 'warning')
        return
      }

      // Request permission (must be called from user interaction)
      const permission = await Notification.requestPermission()
      Logger.debug('Notification permission result:', permission)

      if (permission === 'granted') {
        this.enabledValue = true
        this.currentPermissionValue = 'granted'
        this.updateButtonState()
        this.showAlert('Browser notifications enabled successfully!', 'success')
        
        // Send permission status to backend
        this.updateBackendPermission(true)
      } else {
        this.enabledValue = false
        this.currentPermissionValue = 'denied'
        this.updateButtonState()
        this.showAlert('Browser notifications were denied.', 'warning')
        
        // Send permission status to backend
        this.updateBackendPermission(false)
      }
    } catch (error) {
      Logger.error('Error requesting notification permission:', error)
      this.showAlert('Error enabling browser notifications. Please try again.', 'danger')
    }
  }

  // Test notification
  testNotification() {
    if (Notification.permission === 'granted') {
      new Notification('Test Notification', {
        body: 'This is a test notification from your ERP system.',
        icon: '/assets/logo.png',
        tag: 'test-notification'
      })
      this.playNotificationSound()
      this.showAlert('Test notification sent!', 'success')
    } else {
      this.showAlert('Please enable browser notifications first.', 'warning')
    }
  }

  // Update button state based on permission
  updateButtonState() {
    if (!this.hasButtonTarget) return

    const permission = Notification.permission
    const button = this.buttonTarget
    const icon = this.hasIconTarget ? this.iconTarget : button.querySelector('i')

    // Update button appearance
    if (permission === 'granted') {
      button.classList.remove('btn-outline-secondary', 'btn-outline-warning')
      button.classList.add('btn-success')
      button.title = 'Browser notifications enabled - Click to test'
      if (icon) icon.className = 'fa fa-bell text-white'
    } else if (permission === 'denied') {
      button.classList.remove('btn-outline-secondary', 'btn-success')
      button.classList.add('btn-outline-warning')
      button.title = 'Browser notifications blocked - Click to enable'
      if (icon) icon.className = 'fa fa-bell-slash'
    } else {
      button.classList.remove('btn-success', 'btn-outline-warning')
      button.classList.add('btn-outline-secondary')
      button.title = 'Click to enable browser notifications'
      if (icon) icon.className = 'fa fa-bell'
    }

    // Update status if available
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = this.getPermissionText(permission)
    }
  }

  // Get human-readable permission text
  getPermissionText(permission) {
    switch (permission) {
      case 'granted':
        return 'Notifications enabled'
      case 'denied':
        return 'Notifications blocked'
      default:
        return 'Click to enable'
    }
  }

  // Show alert message
  showAlert(message, type = 'info') {
    // Create alert element
    const alert = document.createElement('div')
    alert.className = `alert alert-${type} alert-dismissible fade show notification-alert`
    alert.innerHTML = `
      ${message}
      <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
    `

    // Add to page
    document.body.appendChild(alert)

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (alert.parentNode) {
        alert.remove()
      }
    }, 5000)
  }

  // Update backend with permission status
  async updateBackendPermission(enabled) {
    try {
      const response = await this.safeFetch('/notifications/browser_notifications', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ enabled: enabled })
      })

      if (response.ok) {
        Logger.debug('Backend permission status updated successfully')
      } else {
        Logger.warn('Failed to update backend permission status')
      }
    } catch (error) {
      Logger.error('Error updating backend permission status:', error)
    }
  }

  // Update sound preference
  async updateSoundPreference(enabled) {
    try {
      const response = await this.safeFetch('/notifications/sound_preference', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ enabled: enabled })
      })

      if (response.ok) {
        Logger.debug('Sound preference updated successfully')
      } else {
        Logger.warn('Failed to update sound preference')
      }
    } catch (error) {
      Logger.error('Error updating sound preference:', error)
    }
  }

  // Handle button click
  handleClick() {
    if (Notification.permission === 'granted') {
      // If already enabled, show test notification
      this.testNotification()
    } else {
      // Request permission
      this.requestPermission()
    }
  }

  // Toggle sound notifications
  toggleSoundNotifications() {
    const currentStatus = this.soundStatusTarget.textContent.trim()
    const newStatus = currentStatus === 'Enabled' ? 'Disabled' : 'Enabled'
    
    this.soundStatusTarget.textContent = newStatus
    
    // Update backend preference
    this.updateSoundPreference(newStatus === 'Enabled')
    
    this.showAlert(`Sound notifications ${newStatus.toLowerCase()}`, 'success')
  }

  // Subscribe to real-time notifications
  subscribeToNotifications() {
    if (window.ActionCable) {
      Logger.debug('Subscribing to notifications channel...')
      this.notificationsSubscription = window.ActionCable.createConsumer()
        .subscriptions.create("NotificationsChannel", {
          received: (data) => this.handleNotification(data)
        })
    } else {
      Logger.warn('ActionCable not available for notifications')
    }
  }

  // Handle incoming notifications
  handleNotification(data) {
    Logger.debug('Received notification:', data)
    
    if (data.type === 'notification') {
      this.showNotificationToast(data.notification)
      this.updateNotificationCount(data.count)
      this.playNotificationSound()
    } else if (data.type === 'notification_count') {
      this.updateNotificationCount(data.count)
    }
  }

  // Show notification toast
  showNotificationToast(notification) {
    // Create toast container if it doesn't exist
    let toastContainer = document.getElementById('notification-toast-container')
    if (!toastContainer) {
      toastContainer = document.createElement('div')
      toastContainer.id = 'notification-toast-container'
      toastContainer.className = 'notification-toast-container'
      document.body.appendChild(toastContainer)
    }

    // Create toast element
    const toastId = `notification-toast-${Date.now()}`
    const toast = document.createElement('div')
    toast.className = 'toast show'
    toast.id = toastId
    toast.innerHTML = `
      <div class="toast-header">
        <i class="fa fa-bell text-primary me-2"></i>
        <strong class="me-auto">${this.getNotificationTitle(notification.action)}</strong>
        <small class="text-muted">${this.formatTime(notification.created_at)}</small>
        <button type="button" class="btn-close" data-bs-dismiss="toast" aria-label="Close"></button>
      </div>
      <div class="toast-body">
        ${this.escapeHtml(notification.message)}
      </div>
    `

    // Add to container
    toastContainer.appendChild(toast)

    // Auto-remove after 5 seconds
    setTimeout(() => {
      const toastElement = document.getElementById(toastId)
      if (toastElement) {
        toastElement.remove()
      }
    }, 5000)
  }

  // notification.message arrives unescaped from the ActionCable server push — never trust it
  // as HTML (matches the pattern already used in toast_controller.js).
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // Update notification count
  updateNotificationCount(count) {
    // Update main count badge
    if (this.hasCountTarget) {
      if (count > 0) {
        this.countTarget.textContent = count
        this.countTarget.classList.remove('d-none')
        this.countTarget.classList.add('d-block')
      } else {
        this.countTarget.classList.add('d-none')
        this.countTarget.classList.remove('d-block')
      }
    }
    
    // Update dropdown count badge
    if (this.hasDropdownCountTarget) {
      if (count > 0) {
        this.dropdownCountTarget.textContent = count
        this.dropdownCountTarget.classList.remove('d-none')
        this.dropdownCountTarget.classList.add('d-block')
      } else {
        this.dropdownCountTarget.classList.add('d-none')
        this.dropdownCountTarget.classList.remove('d-block')
      }
    }
  }

  // Preload notification sound for faster playback
  preloadNotificationSound() {
    // Skip if no sound URL is provided - use oscillator instead
    if (!this.soundUrlValue || this.soundUrlValue.trim() === '') {
      Logger.debug('No sound URL configured, using oscillator fallback')
      this.notificationAudio = null
      return
    }
    
    Logger.debug('Attempting to preload audio from:', this.soundUrlValue)
    
    try {
      this.notificationAudio = new Audio(this.soundUrlValue)
      this.notificationAudio.preload = 'auto'
      this.notificationAudio.volume = this.soundVolumeValue
      
      // Handle load errors gracefully with detailed error info
      this.notificationAudio.addEventListener('error', (e) => {
        const error = this.notificationAudio.error
        const errorMessages = {
          1: 'MEDIA_ERR_ABORTED - The user canceled the audio.',
          2: 'MEDIA_ERR_NETWORK - A network error occurred.',
          3: 'MEDIA_ERR_DECODE - The audio playback was aborted due to a corruption problem.',
          4: 'MEDIA_ERR_SRC_NOT_SUPPORTED - The audio format is not supported.'
        }
        const errorCode = error ? error.code : 'unknown'
        const errorMessage = errorMessages[errorCode] || `Unknown error (code: ${errorCode})`
        Logger.debug('Audio file failed to load:', errorMessage)
        Logger.debug('Audio URL:', this.soundUrlValue)
        this.notificationAudio = null
      })
      
      // Handle successful load
      this.notificationAudio.addEventListener('canplaythrough', () => {
        Logger.debug('Notification sound preloaded successfully')
        this.audioReady = true
      })

      // Safari fix: try loading on user interaction if autoload fails
      this.notificationAudio.addEventListener('loadstart', () => {
        Logger.debug('Audio load started')
      })
      
      // Start loading the audio file
      this.notificationAudio.load()
    } catch (error) {
      Logger.debug('Failed to preload notification sound:', error.message)
      this.notificationAudio = null
    }
  }

  // Play notification sound with HTML5 Audio fallback to Web Audio API
  playNotificationSound() {
    // Check if sound notifications are enabled
    if (this.hasSoundStatusTarget && this.soundStatusTarget.textContent.trim() === 'Disabled') {
      return
    }
    
    // Try HTML5 Audio first (preferred for actual sound files)
    if (this.notificationAudio) {
      this.playAudioFile()
    } else {
      // Fallback to Web Audio API oscillator
      this.playOscillatorSound()
    }
  }

  // Play notification using HTML5 Audio
  playAudioFile() {
    try {
      // Clone the audio element for overlapping sounds
      const audio = this.notificationAudio.cloneNode()
      audio.volume = this.soundVolumeValue
      
      audio.play().then(() => {
        Logger.debug('Notification sound played (audio file)')
      }).catch((error) => {
        Logger.debug('Audio file playback failed, falling back to oscillator:', error.message)
        this.playOscillatorSound()
      })
    } catch (error) {
      Logger.debug('Audio file error, falling back to oscillator:', error.message)
      this.playOscillatorSound()
    }
  }

  // Fallback: Play notification using Web Audio API oscillator
  playOscillatorSound() {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)

      // Pleasant notification sound pattern
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime)
      oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1)
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.2)

      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime)
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3)

      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + 0.3)
      
      Logger.debug('Notification sound played (oscillator)')
    } catch (error) {
      Logger.debug('Notification sound not available:', error.message)
    }
  }

  // Get notification title based on action
  getNotificationTitle(action) {
    const titles = {
      'message_created': 'New Message',
      'message_mentioned': 'Mention',
      'file_shared': 'File Shared',
      'channel_invited': 'Channel Invitation',
      'reaction_added': 'Reaction',
      'content_flagged': 'Content Flagged',
      'channel_joined': 'Channel Joined',
      'channel_left': 'Channel Left',
      'conversation_started': 'New Conversation',
      'participant_added': 'Participant Added',
      'participant_removed': 'Participant Removed',
      'digest': 'Digest',
      'system_announcement': 'System Announcement',
      'channel_archived': 'Channel Archived',
      'conversation_archived': 'Conversation Archived'
    }
    return titles[action] || 'Notification'
  }

  // Format time for display
  formatTime(timestamp) {
    const date = new Date(timestamp)
    const now = new Date()
    const diff = now - date

    if (diff < 60000) { // Less than 1 minute
      return 'Just now'
    } else if (diff < 3600000) { // Less than 1 hour
      const minutes = Math.floor(diff / 60000)
      return `${minutes}m ago`
    } else if (diff < 86400000) { // Less than 1 day
      const hours = Math.floor(diff / 3600000)
      return `${hours}h ago`
    } else {
      return date.toLocaleDateString()
    }
  }
}
